<template>
  <v-card v-if="rows.length" rounded="lg">
    <v-card-title class="text-subtitle-1 pb-0">
      Geometries ({{ rows.length }})
    </v-card-title>
    <v-card-subtitle class="pt-1 pb-2">
      ε = relative permittivity, n = √ε (refractive index).
      <template v-if="!is3d">Positions and sizes in µm, measured from the center of the cell.</template>
      <template v-else>Positions (center) and sizes in µm.</template>
    </v-card-subtitle>
    <v-data-table
      :headers="headers"
      :items="rows"
      :items-per-page="$vuetify.breakpoint.xsOnly ? 5 : 10"
      :footer-props="{ itemsPerPageOptions: [5, 10, 25, 50, -1] }"
      dense
      class="details-geometries"
    >
      <template v-slot:[`item.name`]="{ item }">
        <span class="d-inline-flex align-center">
          <span class="swatch mr-2" :style="{ background: item.fill }" />
          {{ item.name }}
        </span>
      </template>
    </v-data-table>
  </v-card>
</template>
<script>
import { geometryToMeep } from "@/store/module-simulator/mutations";

const SHAPES_2D = { block: "Block", sphere: "Circle", triangle: "Triangle" };
const NORMALIZATION = { device: "Device", both: "Device + reference", reference: "Reference only" };

const round = (value, digits = 3) => {
  const number = Number(value);
  return Number.isFinite(number) ? Number(number.toFixed(digits)) : "-";
};
const capitalize = (text) => String(text || "").charAt(0).toUpperCase() + String(text || "").slice(1);

export default {
  name: "DetailsGeometryCard",
  props: {
    geometries: { type: Array, default: () => [] },
    // Estado salvo na produção 2D (célula e escala do canvas), para converter pixels em µm
    production: { type: Object, default: null },
    is3d: { type: Boolean, default: false }
  },
  computed: {
    headers() {
      const headers = [
        { text: "Name", value: "name" },
        { text: "Shape", value: "shape" },
        { text: "Material", value: "material" },
        { text: "ε", value: "epsilon" },
        { text: "n", value: "n" },
        { text: "Center (µm)", value: "center", sortable: false },
        { text: "Size (µm)", value: "size", sortable: false }
      ];
      if (!this.is3d) headers.push({ text: "Used in", value: "normalization" });
      return headers;
    },
    rows() {
      return this.geometries.map((geometry, index) =>
        this.is3d ? this.row3d(geometry, index) : this.row2d(geometry, index)
      );
    }
  },
  methods: {
    base(geometry, index) {
      const epsilon = Number(geometry.epsilon) || 1;
      return {
        key: geometry.token || index,
        name: geometry.name || `Geometry ${index + 1}`,
        fill: geometry.fill || "#888",
        material: geometry.materialTitle || "Custom",
        epsilon: round(epsilon, 4),
        n: round(Math.sqrt(epsilon), 4)
      };
    },
    row2d(geometry, index) {
      const row = { ...this.base(geometry, index), shape: SHAPES_2D[geometry.shape] || capitalize(geometry.shape) };
      row.normalization = NORMALIZATION[geometry.normalization || "device"];
      try {
        const { position, size } = geometryToMeep(this.production, geometry);
        row.center = `(${round(position[0])}, ${round(position[1])})`;
        row.size = geometry.shape === "block"
          ? `${round(size[0])} × ${round(size[1])}`
          : `r = ${round(size[0])}`;
      } catch (err) {
        row.center = "-";
        row.size = "-";
      }
      return row;
    },
    row3d(geometry, index) {
      const row = { ...this.base(geometry, index), shape: capitalize(geometry.shape) };
      row.center = `(${round(geometry.x)}, ${round(geometry.y)}, ${round(geometry.z)})`;
      row.size = geometry.shape === "sphere"
        ? `r = ${round(Math.abs(geometry.radius))}`
        : `${round(Math.abs(geometry.width))} × ${round(Math.abs(geometry.height))} × ${round(Math.abs(geometry.depth))}`;
      return row;
    }
  }
};
</script>
<style scoped>
.swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #ffffff55;
  flex-shrink: 0;
}
</style>
